/**
 * Etiquetas de código de barras
 *
 * Se eligen los artículos y cuántas etiquetas de cada uno; sale una hoja
 * Avery 5160 o una hoja de mostrador, para imprimir o bajar en PDF. El código
 * impreso es siempre el interno (ver `articleBarcode`), que el lector del POS
 * reconoce sin tocar el código alterno.
 */

import { useEffect, useMemo, useRef, useState, type ComponentProps } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { ArrowLeft, Check, ClipboardList, Download, Minus, PackageCheck, Plus, Printer, Search, Tags, Trash2, X } from 'lucide-react'

import api from '@/lib/api'
import { articleBarcode } from '@/lib/barcode'
import { useBarcodeScanner } from '@/lib/barcodeScanner'
import {
  buildSheet,
  fitsLabel,
  isRoll,
  LABELS_PER_PAGE,
  pageSize,
  sheetToPdf,
  sheetToPrintHtml,
  sheetToSvg,
  type LabelItem,
  type Shape,
  type SheetFormat,
} from '@/lib/labelSheet'
import { debounce, formatCurrency } from '@/lib/utils'
import { t } from '@/i18n'
import { useBusinessInfo } from '@/hooks/useBusinessInfo'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/components/ui/toaster'
import type { Article } from '@/types'

export interface Selected {
  id: number
  code: string
  description: string
  price: number
  stock: number
  quantity: number
}

const MAX_QUANTITY = 999
const PREVIEW_PAGES = 2
// En rollo cada página es una etiqueta: se muestran varias en cuadrícula.
const PREVIEW_ROLL_LABELS = 12
const FORMAT_KEY = 'labels.format'
const FORMATS: SheetFormat[] = ['roll50x26', 'roll35x26', 'roll50x50', 'avery5160', 'counter']

function savedFormat(): SheetFormat {
  try {
    const value = localStorage.getItem(FORMAT_KEY) as SheetFormat | null
    if (value && FORMATS.includes(value)) return value
  } catch {
    // Sin almacenamiento (incógnito, bloqueado): se usa el principal.
  }
  return 'roll50x26'
}

/** Cantidad de etiquetas de un artículo, entre 1 y el tope de la pantalla. */
export function clampQuantity(quantity: number): number {
  return Math.max(1, Math.min(MAX_QUANTITY, Math.floor(quantity) || 1))
}

/** Una etiqueta por unidad en existencia; si no hay existencias, una. */
function suggestedQuantity(stock: number): number {
  return clampQuantity(stock)
}

/** Lo que manda el asistente de inventario: un lote con la cantidad que falta de cada artículo. */
export interface LabelsLocationState {
  items: Selected[]
  fromInventory: boolean
  /** Dirección del asistente con sus filtros, para volver al mismo lugar. */
  returnTo?: string
}

/**
 * Impresión ya registrada. El navegador no avisa si salió: se da por impresa y
 * la persona solo avisa cuando no salió, para quitar el registro.
 */
interface RecordedPrint {
  ids: number[]
  labels: number
}

function toSelected(article: Article): Selected {
  const stock = Math.max(0, Math.floor(Number(article.count_articles) || 0))
  return {
    id: article.id,
    code: article.code,
    description: article.description,
    price: Number(article.sale_price_1_iva),
    stock,
    quantity: suggestedQuantity(stock),
  }
}

function SheetPreview({ shapes, index, format }: { shapes: Shape[]; index: number; format: SheetFormat }) {
  // El mismo SVG que se imprime; los textos van escapados.
  const svg = useMemo(() => sheetToSvg(shapes, pageSize(format), true), [shapes, format])
  const size = pageSize(format)
  return (
    <div
      className="w-full bg-white shadow-sm ring-1 ring-border rounded-sm"
      style={{ aspectRatio: `${size.width} / ${size.height}` }}
      role="img"
      aria-label={`${t('labels.preview')} ${index + 1}`}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  )
}

/**
 * Campo numérico entero con su propio borrador: se puede vaciar para escribir
 * otro número sin que salte a 1 a mitad de camino. Al salir queda acotado.
 */
function IntegerInput({
  value,
  min,
  max,
  onCommit,
  ...props
}: {
  value: number
  min: number
  max: number
  onCommit: (value: number) => void
} & Omit<ComponentProps<typeof Input>, 'value' | 'onChange' | 'onBlur' | 'min' | 'max'>) {
  const [draft, setDraft] = useState(String(value))
  useEffect(() => setDraft(String(value)), [value])
  const clamp = (n: number) => Math.max(min, Math.min(max, Math.floor(n)))
  return (
    <Input
      {...props}
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value)
        const n = Number(e.target.value)
        if (e.target.value !== '' && Number.isFinite(n) && n >= min) onCommit(clamp(n))
      }}
      onBlur={() => {
        const n = Number(draft)
        const committed = draft !== '' && Number.isFinite(n) ? clamp(n) : value
        setDraft(String(committed))
        if (committed !== value) onCommit(committed)
      }}
    />
  )
}

export function ArticleLabelsPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [searchParams] = useSearchParams()
  const { data: business } = useBusinessInfo()

  const location = useLocation()
  const queryClient = useQueryClient()
  const fromState = location.state as LabelsLocationState | null
  const fromInventory = !!fromState?.fromInventory
  const [selected, setSelected] = useState<Selected[]>(() =>
    (fromState?.items ?? []).map((s) => ({ ...s, quantity: clampQuantity(s.quantity) }))
  )
  const [recordedPrint, setRecordedPrint] = useState<RecordedPrint | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [format, setFormatState] = useState<SheetFormat>(savedFormat)
  const [showPrice, setShowPrice] = useState(true)
  const [startAt, setStartAt] = useState(1)
  const printFrameRef = useRef<HTMLIFrameElement | null>(null)

  const setFormat = (value: SheetFormat) => {
    setFormatState(value)
    try {
      localStorage.setItem(FORMAT_KEY, value)
    } catch {
      // Recordar el formato es solo una comodidad.
    }
  }

  // Coincidencias exactas de un código compartido (lookup con más de una).
  const [lookupMatches, setLookupMatches] = useState<Article[] | null>(null)
  const searchInputRef = useRef('')

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        // Si mientras esperaba se agregó el artículo y se vació el buscador, no revivir la búsqueda.
        if (searchInputRef.current === value) setSearchQuery(value.trim())
      }, 300),
    []
  )
  const changeSearch = (value: string) => {
    searchInputRef.current = value
    setSearchInput(value)
    setLookupMatches(null)
  }

  // Con artículos ya elegidos: /articles/labels?ids=12,15
  const initialIds = searchParams.get('ids')
  useEffect(() => {
    if (!initialIds) return
    const ids = [...new Set(initialIds.split(',').map(Number))].filter((id) => Number.isInteger(id) && id > 0)
    Promise.all(ids.map((id) => api.get<Article>(`/articles/${id}`).then((r) => r.data).catch(() => null))).then(
      (articles) => {
        const loaded = articles.filter((a): a is Article => !!a)
        // Se suman a lo que ya haya: mientras cargaban se pudo agregar otro.
        setSelected((current) => [
          ...current,
          ...loaded.filter((a) => !current.some((s) => s.id === a.id)).map(toSelected),
        ])
        if (loaded.length < ids.length) {
          toast({ title: t('labels.loadFailed', { count: ids.length - loaded.length }), variant: 'destructive' })
        }
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIds])

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['articles', 'labels-search', searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams({ search: searchQuery, page: '1', page_size: '8' })
      const response = await api.get(`/articles/?${params}`)
      return response.data.items as Article[]
    },
    enabled: searchQuery.length > 0,
  })

  const suggestions = lookupMatches ?? results

  const add = (article: Article) => {
    const existing = selected.some((s) => s.id === article.id)
    toast({ title: t(existing ? 'labels.addedAgain' : 'labels.added', { code: article.code }) })
    setSelected((current) =>
      current.some((s) => s.id === article.id)
        ? current.map((s) =>
            s.id === article.id ? { ...s, quantity: Math.min(s.quantity + 1, MAX_QUANTITY) } : s
          )
        : [...current, toSelected(article)]
    )
    changeSearch('')
    setSearchQuery('')
  }

  // Enter en el buscador (o el lector): coincidencia exacta por código.
  const addByCode = async (scanned?: string) => {
    const code = (scanned ?? searchInput).trim()
    if (!code) return
    try {
      const response = await api.get<{ items: Article[] }>('/articles/lookup', { params: { code } })
      if (response.data.items.length === 1) {
        add(response.data.items[0])
        return
      }
      // Varios comparten el código: se elige entre esos, no entre los que lo "contienen".
      setLookupMatches(response.data.items)
    } catch (error) {
      const notFound = isAxiosError(error) && error.response?.status === 404
      toast({
        title: notFound ? t('labels.notFound', { code }) : t('common.errorOccurred'),
        variant: 'destructive',
      })
    }
  }

  // Lo leído reemplaza lo que hubiera escrito en el buscador, no se le pega al final.
  useBarcodeScanner((code) => {
    changeSearch('')
    setSearchQuery('')
    void addByCode(code)
  })

  const setQuantity = (id: number, quantity: number) => {
    const value = clampQuantity(quantity)
    setSelected((current) => current.map((s) => (s.id === id ? { ...s, quantity: value } : s)))
  }

  const [printable, unprintable] = useMemo(
    () => [selected.filter((s) => articleBarcode(s.code)), selected.filter((s) => !articleBarcode(s.code))],
    [selected]
  )
  const tooLong = useMemo(
    () =>
      printable.filter((s) => !fitsLabel(articleBarcode(s.code)!, format)).map((s) => s.code),
    [printable, format]
  )
  const perPage = LABELS_PER_PAGE[format]
  const skip = format === 'avery5160' ? startAt - 1 : 0

  const items: LabelItem[] = useMemo(
    () =>
      printable.flatMap((s) =>
        Array.from({ length: s.quantity }, () => ({
          code: s.code,
          description: s.description,
          price: showPrice ? s.price : null,
        }))
      ),
    [printable, showPrice]
  )
  const businessName = business?.name ?? ''
  const roll = isRoll(format)
  const pageCount = items.length === 0 ? 0 : roll ? items.length : Math.ceil((items.length + skip) / perPage)
  const previewCount = roll ? PREVIEW_ROLL_LABELS : PREVIEW_PAGES
  // Un lote del asistente puede ser de miles de etiquetas: la vista previa se arma
  // solo con lo que muestra y el lote entero recién al imprimir o descargar.
  const sheetFor = (labels: LabelItem[]) =>
    buildSheet(
      labels,
      { format, businessName, skip },
      { title: t('labels.sheetTitle', { business: businessName }), hint: t('labels.counterHint') }
    )
  const previewPages = useMemo(
    () => sheetFor(items.slice(0, roll ? PREVIEW_ROLL_LABELS : PREVIEW_PAGES * perPage - skip)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, format, businessName, skip]
  )

  const pdfBlob = () =>
    new Blob([sheetToPdf(sheetFor(items), t('labels.sheetTitle', { business: businessName }), pageSize(format))], {
      type: 'application/pdf',
    })

  const recordPrints = useMutation({
    mutationFn: async (lines: { article_id: number; quantity: number }[]) =>
      (await api.post<{ ids: number[]; labels: number }>('/articles/label-prints', { items: lines })).data,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['label-status'] })
      setRecordedPrint({ ids: result.ids, labels: result.labels })
    },
    onError: () => toast({ title: t('common.errorOccurred'), variant: 'destructive' }),
  })

  const undoPrints = useMutation({
    mutationFn: (record: RecordedPrint) => api.post('/articles/label-prints/undo', { ids: record.ids }),
    onSuccess: (_, record) => {
      queryClient.invalidateQueries({ queryKey: ['label-status'] })
      toast({ title: t('labels.undone', { count: record.labels }) })
      setRecordedPrint(null)
    },
    onError: () => toast({ title: t('common.errorOccurred'), variant: 'destructive' }),
  })

  const recordPrint = () => {
    setRecordedPrint(null)
    recordPrints.mutate(printable.map((s) => ({ article_id: s.id, quantity: s.quantity })))
  }

  const finishPrint = () => {
    setRecordedPrint(null)
    // replace: con Atrás no se vuelve a cargar el lote ya registrado.
    if (fromInventory) navigate(fromState?.returnTo ?? '/articles/labels/inventory', { replace: true })
  }

  const downloadPdf = () => {
    const url = URL.createObjectURL(pdfBlob())
    const link = document.createElement('a')
    link.href = url
    link.download = `${t('labels.fileName')}-${new Date().toISOString().slice(0, 10)}.pdf`
    link.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
    recordPrint()
  }

  // El PDF del iframe vive hasta la próxima impresión o hasta salir de la pantalla.
  const removePrintFrame = () => {
    printFrameRef.current?.remove()
    printFrameRef.current = null
  }

  const openPdfInTab = () => {
    const url = URL.createObjectURL(pdfBlob())
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }

  const print = () => {
    recordPrint()
    // En tablet y celular imprimir un iframe no es confiable: se abre el PDF en otra pestaña.
    if (window.matchMedia('(any-pointer: coarse)').matches) {
      openPdfInTab()
      return
    }
    // Página propia con @page (tamaño exacto, margen 0, escala 100 %), no el PDF:
    // con el PDF el navegador elige márgenes y escala por su cuenta.
    removePrintFrame()
    const frame = document.createElement('iframe')
    frame.style.cssText = 'position:fixed;width:0;height:0;border:0;right:0;bottom:0'
    frame.srcdoc = sheetToPrintHtml(sheetFor(items), pageSize(format), t('labels.sheetTitle', { business: businessName }))
    frame.onload = () => {
      try {
        frame.contentWindow?.focus()
        frame.contentWindow?.print()
      } catch {
        openPdfInTab()
      }
    }
    document.body.appendChild(frame)
    printFrameRef.current = frame
  }

  useEffect(() => removePrintFrame, [])

  const formatOptions: { value: SheetFormat; label: string; hint: string }[] = [
    { value: 'roll50x26', label: t('labels.formatRoll50x26'), hint: t('labels.formatRollHint') },
    { value: 'roll35x26', label: t('labels.formatRoll35x26'), hint: t('labels.formatRollSmallHint') },
    { value: 'roll50x50', label: t('labels.formatRoll50x50'), hint: t('labels.formatRollSquareHint') },
    { value: 'avery5160', label: t('labels.formatAvery'), hint: t('labels.formatAveryHint') },
    { value: 'counter', label: t('labels.formatCounter'), hint: t('labels.formatCounterHint') },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0" onClick={() => navigate('/articles')} aria-label={t('common.back')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl md:text-2xl font-bold">{t('labels.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('labels.subtitle')}</p>
        </div>
        <Button variant="outline" asChild className="shrink-0">
          <Link to="/articles/labels/inventory">
            <ClipboardList className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">{t('labels.inventoryOpen')}</span>
            <span className="sr-only md:hidden">{t('labels.inventoryOpen')}</span>
          </Link>
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,5fr)_minmax(0,4fr)]">
        <div className="space-y-4 min-w-0">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Tags className="h-4 w-4" />
                {t('labels.articles')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  placeholder={t('labels.searchPlaceholder')}
                  className="pl-10 h-12 text-base"
                  value={searchInput}
                  onChange={(e) => {
                    changeSearch(e.target.value)
                    debouncedSearch(e.target.value)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      void addByCode()
                    }
                  }}
                />
                {searchInput && (lookupMatches || searchQuery) && (
                  <div className="absolute z-20 mt-1 w-full rounded-md border bg-popover shadow-md max-h-80 overflow-y-auto">
                    {suggestions.length === 0 ? (
                      <p className="p-3 text-sm text-muted-foreground">{isFetching ? t('common.loading') : t('labels.noResults')}</p>
                    ) : (
                      suggestions.map((article) => (
                        <button
                          key={article.id}
                          type="button"
                          className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                          onClick={() => add(article)}
                        >
                          <span className="min-w-0">
                            <span className="block font-medium truncate">{article.description}</span>
                            <span className="block text-xs text-muted-foreground">
                              {article.code} · {t('labels.stock', { count: Math.floor(Number(article.count_articles) || 0) })}
                            </span>
                          </span>
                          <Plus className="h-4 w-4 shrink-0 text-muted-foreground" />
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {selected.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">{t('labels.empty')}</p>
              ) : (
                <>
                  <ul className="divide-y rounded-md border">
                    {selected.map((s) => (
                      <li key={s.id} className="flex flex-wrap items-center gap-2 p-2 sm:flex-nowrap sm:p-3">
                        <div className="min-w-0 basis-full sm:basis-auto sm:flex-1">
                          <p className="font-medium truncate">{s.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {s.code} · {formatCurrency(s.price)} ·{' '}
                            <span className={s.stock > 0 ? 'font-medium text-foreground' : 'text-destructive'}>
                              {t('labels.stock', { count: s.stock })}
                            </span>
                            {s.stock > 0 && s.quantity !== suggestedQuantity(s.stock) && (
                              <>
                                {' '}
                                <button
                                  type="button"
                                  className="text-primary underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none"
                                  onClick={() => setQuantity(s.id, suggestedQuantity(s.stock))}
                                >
                                  {t('labels.useStock')}
                                </button>
                              </>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 ml-auto">
                          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setQuantity(s.id, s.quantity - 1)} aria-label={t('pos.decreaseQuantity')}>
                            <Minus className="h-4 w-4" />
                          </Button>
                          <IntegerInput
                            min={1}
                            max={MAX_QUANTITY}
                            value={s.quantity}
                            onCommit={(value) => setQuantity(s.id, value)}
                            className="h-9 w-14 text-center px-1"
                            aria-label={t('labels.quantity')}
                          />
                          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setQuantity(s.id, s.quantity + 1)} aria-label={t('pos.increaseQuantity')}>
                            <Plus className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 text-muted-foreground hover:text-destructive"
                            onClick={() => setSelected((current) => current.filter((x) => x.id !== s.id))}
                            aria-label={t('labels.remove')}
                            title={t('labels.remove')}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setSelected((current) => current.map((x) => ({ ...x, quantity: suggestedQuantity(x.stock) })))
                      }
                    >
                      <PackageCheck className="h-4 w-4 mr-1" />
                      {t('labels.allToStock')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setSelected([])}>
                      <Trash2 className="h-4 w-4 mr-1" />
                      {t('labels.removeAll')}
                    </Button>
                  </div>
                </>
              )}
              {tooLong.length > 0 && (
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  {t('labels.tooLong', { codes: tooLong.join(', ') })}
                </p>
              )}
              {unprintable.length > 0 && (
                <p className="text-sm text-destructive">
                  {t('labels.unprintable', { codes: unprintable.map((s) => s.code).join(', ') })}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('labels.format')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label={t('labels.format')}>
                {formatOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={format === option.value}
                    onClick={() => setFormat(option.value)}
                    className={`rounded-md border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      format === option.value ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/50'
                    }`}
                  >
                    <span className="block font-medium">{option.label}</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">{option.hint}</span>
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="labels-show-price">{t('labels.showPrice')}</Label>
                <Switch id="labels-show-price" checked={showPrice} onCheckedChange={setShowPrice} />
              </div>

              {format === 'avery5160' && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor="labels-start-at">{t('labels.startAt')}</Label>
                    <IntegerInput
                      id="labels-start-at"
                      min={1}
                      max={perPage}
                      value={startAt}
                      onCommit={setStartAt}
                      className="h-9 w-20 text-center"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('labels.startAtHint')}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-3 min-w-0 lg:sticky lg:top-4 lg:self-start">
          <Card>
            <CardContent className="p-4 space-y-3">
              <p className="text-sm font-medium">
                {roll
                  ? t(items.length === 1 ? 'labels.summaryRollOne' : 'labels.summaryRoll', { labels: items.length })
                  : t(items.length === 1 ? 'labels.summaryOne' : pageCount === 1 ? 'labels.summaryOnePage' : 'labels.summary', {
                      labels: items.length,
                      pages: pageCount,
                    })}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Button className="h-11" onClick={print} disabled={items.length === 0}>
                  <Printer className="h-4 w-4 mr-2" />
                  {t('labels.print')}
                </Button>
                <Button variant="outline" className="h-11" onClick={downloadPdf} disabled={items.length === 0}>
                  <Download className="h-4 w-4 mr-2" />
                  {t('labels.downloadPdf')}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{t(roll ? 'labels.printTipRoll' : 'labels.printTip')}</p>
            </CardContent>
          </Card>

          {recordedPrint && (
            <Card className="border-primary" role="status">
              <CardContent className="p-4 space-y-3">
                <p className="font-medium">{t('labels.printedTitle', { count: recordedPrint.labels })}</p>
                <p className="text-xs text-muted-foreground">{t('labels.printedHint')}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button className="h-11" onClick={finishPrint} disabled={undoPrints.isPending}>
                    <Check className="h-4 w-4 mr-2" />
                    {t('labels.printedDone')}
                  </Button>
                  <Button
                    variant="outline"
                    className="h-11"
                    onClick={() => undoPrints.mutate(recordedPrint)}
                    disabled={undoPrints.isPending}
                  >
                    {t('labels.confirmNo')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {previewPages.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-muted-foreground">{t('labels.preview')}</p>
              {roll ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {previewPages.map((shapes, index) => (
                    <SheetPreview key={index} shapes={shapes} index={index} format={format} />
                  ))}
                </div>
              ) : (
                previewPages.map((shapes, index) => (
                  <SheetPreview key={index} shapes={shapes} index={index} format={format} />
                ))
              )}
              {pageCount > previewCount && (
                <p className="text-sm text-muted-foreground">
                  {t(roll ? 'labels.moreLabels' : 'labels.morePages', {
                    count: pageCount - previewCount,
                  })}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
